import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { EditSvg } from "../../svgFiles/EditSvg";
import { UndoSvg } from "../../svgFiles/UndoSvg";

const emptyDelivery = {
  rawShipTo: "",
  firstName: "",
  lastName: "",
  clinicName: "",
  hospitalName: "",
  audiologistName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
  email: "",
  poNumber: "",
};

function formatAttnPo(poNumber) {
  return String(poNumber || "")
    .trim()
    .replace(/^(p\.?\s*o\.?\s*#?\s*)+/i, "")
    .replace(/^ATTN\s*PO:\s*/i, "")
    .trim();
}

function deliverySnapshot(delivery = {}) {
  return { ...emptyDelivery, ...delivery };
}

function splitRawShipToLines(rawShipTo = "") {
  return String(rawShipTo || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/g, ""))
    .filter((line) => line.trim().length > 0);
}

function formatCityStateZip({ city, state, zip } = {}) {
  const right = [state, zip].filter(Boolean).join(" ");
  return [city, right].filter(Boolean).join(", ");
}

function parseCityStateZip(value) {
  const raw = String(value || "").trim();
  if (!raw) return { city: "", state: "", zip: "" };

  const withComma = raw.match(
    /^(.+?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/
  );
  if (withComma) {
    return {
      city: withComma[1].trim(),
      state: withComma[2].toUpperCase(),
      zip: withComma[3],
    };
  }

  const withoutComma = raw.match(
    /^(.+?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/
  );
  if (withoutComma) {
    return {
      city: withoutComma[1].trim(),
      state: withoutComma[2].toUpperCase(),
      zip: withoutComma[3],
    };
  }

  return { city: raw, state: "", zip: "" };
}

function looksLikeCityStateZipLine(value) {
  return /\b[A-Za-z]{2}\s+\d{5}(?:-\d{4})?\b/.test(String(value || "").trim());
}

/** Prefer structured city/state/zip; split blobs like "City, ST ZIP" when needed. */
function resolveCityStateZip(base = {}, cityLine = "") {
  let city = String(base.city || "").trim();
  let state = String(base.state || "").trim().toUpperCase();
  let zip = String(base.zip || "").trim();

  if (looksLikeCityStateZipLine(city) && (!state || !zip)) {
    const fromCity = parseCityStateZip(city);
    city = fromCity.city;
    state = state || fromCity.state;
    zip = zip || fromCity.zip;
  }

  if ((!city || !state || !zip) && cityLine) {
    const fromLine = parseCityStateZip(cityLine);
    city = city || fromLine.city;
    state = state || fromLine.state;
    zip = zip || fromLine.zip;
  }

  return { city, state, zip };
}

function recipientFromDelivery(delivery = {}) {
  return (
    String(delivery?.audiologistName || "").trim() ||
    [delivery?.firstName, delivery?.lastName].filter(Boolean).join(" ")
  );
}

function facilityFromDelivery(delivery = {}) {
  return (
    String(delivery?.clinicName || "").trim() ||
    String(delivery?.hospitalName || "").trim()
  );
}

function toEditForm(delivery = {}, poNumber = "") {
  const base = deliverySnapshot(delivery);
  const rawLines = splitRawShipToLines(base.rawShipTo);
  const attnFromRaw = rawLines.find((line) =>
    /^(ATTN\s*PO:|P\.?\s*O\.?\s*#?)/i.test(line)
  );
  // If SHIP TO raw exists without an ATTN/PO line, keep ATTN empty — do not
  // revive page/order PO into the shipping form.
  const cleanedPo = formatAttnPo(
    attnFromRaw ||
      (rawLines.length > 0 ? "" : poNumber || base.poNumber)
  );
  const attnPo = cleanedPo
    ? /^P\.?\s*O\.?\s*#?/i.test(String(attnFromRaw || ""))
      ? `P.O.# ${cleanedPo}`
      : `ATTN PO: ${cleanedPo}`
    : "";

  // Prefer printed SHIP TO layout when raw text exists
  if (rawLines.length >= 4) {
    const withoutPo = rawLines.filter(
      (line) => !/^(ATTN\s*PO:|P\.?\s*O\.?\s*#?)/i.test(line)
    );
    const phoneIdx = withoutPo.findIndex((line) =>
      /\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/.test(line)
    );
    const phone =
      phoneIdx >= 0 ? withoutPo[phoneIdx] : String(base.phone || "").trim();
    const beforePhone =
      phoneIdx >= 0 ? withoutPo.slice(0, phoneIdx) : withoutPo;
    const cityLine =
      beforePhone.length >= 1
        ? beforePhone[beforePhone.length - 1]
        : formatCityStateZip(base);
    const address1 =
      beforePhone.length >= 2
        ? beforePhone[beforePhone.length - 2]
        : String(base.address1 || "").trim();
    const recipient =
      beforePhone.length >= 3
        ? beforePhone[beforePhone.length - 3]
        : recipientFromDelivery(base);
    const facilityName =
      beforePhone.length >= 4
        ? beforePhone.slice(0, beforePhone.length - 3).join(" ")
        : beforePhone.length > 3
          ? beforePhone[0]
          : facilityFromDelivery(base);

    const { city, state, zip } = resolveCityStateZip(base, cityLine);

    return {
      ...base,
      facilityName,
      attnPo,
      recipient,
      address1,
      city,
      state,
      zip,
      phone,
    };
  }

  const { city, state, zip } = resolveCityStateZip(
    base,
    formatCityStateZip(base)
  );

  return {
    ...base,
    facilityName: facilityFromDelivery(base),
    attnPo,
    recipient: recipientFromDelivery(base),
    city,
    state,
    zip,
  };
}

/**
 * Prefer exact PDF rawShipTo. Fallback rebuilds every structured part
 * (facility, full audiologist line, ATTN PO, street, city, phone).
 */
export function buildShipToDisplayLines(delivery = {}, poNumber = "") {
  const rawLines = splitRawShipToLines(delivery?.rawShipTo);
  if (rawLines.length > 0) {
    return rawLines;
  }

  const hospital = String(delivery?.hospitalName || "").trim();
  const clinic = String(delivery?.clinicName || "").trim();
  // One facility line only — hospital+clinic broke ATTN PO parse / change logs.
  const facility = clinic || hospital;
  const personLine = recipientFromDelivery(delivery);
  const po = formatAttnPo(poNumber || delivery?.poNumber);
  const street1 = String(delivery?.address1 || "").trim();
  const street2 = String(delivery?.address2 || "").trim();
  const city = String(delivery?.city || "").trim();
  const state = String(delivery?.state || "").trim();
  const zip = String(delivery?.zip || "").trim();
  const country = String(delivery?.country || "").trim();
  const phone = String(delivery?.phone || "").trim();

  return [
    facility,
    po ? `ATTN PO: ${po}` : "",
    personLine,
    street1,
    street2,
    city,
    state,
    zip,
    country,
    phone,
  ].filter(Boolean);
}

export function getDeliveryFieldErrors(form = {}) {
  // Use structured fields as edited — do not re-expand from rawShipTo
  // (that would refill values the admin just cleared).
  const address1 = String(form.address1 ?? form.Address1 ?? "").trim();
  const zip = String(form.zip ?? form.ShippingZip ?? "").trim();

  const errors = {};
  if (!address1) errors.address1 = true;
  if (!zip) errors.zip = true;
  return errors;
}

/** True when every required delivery field is present. */
export function hasDeliveryAddress(delivery = {}) {
  return Object.keys(getDeliveryFieldErrors(delivery)).length === 0;
}

function buildDeliveryPayload(form, poNumber) {
  const facility = String(form.facilityName || "").trim();
  const recipient = String(form.recipient || "").trim();
  // If attnPo is present on the form (including ""), respect it — don't revive page PO.
  const attnPo = formatAttnPo(
    Object.prototype.hasOwnProperty.call(form, "attnPo")
      ? form.attnPo
      : poNumber
  );
  const structured = {
    ...deliverySnapshot(form),
    hospitalName: "",
    clinicName: facility,
    audiologistName: recipient,
    firstName: recipient,
    lastName: "",
    address1: String(form.address1 || "").trim(),
    address2: String(form.address2 || "").trim(),
    city: String(form.city || "").trim(),
    state: String(form.state || "").trim().toUpperCase(),
    zip: String(form.zip || "").trim(),
    country: String(form.country || "").trim(),
    phone: String(form.phone || "").trim(),
    poNumber: attnPo,
  };

  // Rebuild SHIP TO from current fields only so cleared values stay cleared.
  const rawShipTo = buildShipToDisplayLines(
    { ...structured, rawShipTo: "" },
    attnPo
  ).join("\n");

  return {
    ...structured,
    rawShipTo,
  };
}

export default function DeliverySummary({
  delivery,
  onSave,
  poNumber = "",
  onPoNumberChange,
  startEditing = false,
  forceValidate = false,
}) {
  const [editing, setEditing] = useState(Boolean(startEditing));
  const [form, setForm] = useState(() => toEditForm(delivery, poNumber));
  const [baseline, setBaseline] = useState(() => deliverySnapshot(delivery));
  const [baselinePo, setBaselinePo] = useState(() => formatAttnPo(poNumber));
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!editing) {
      setForm(toEditForm(delivery, poNumber));
      setBaseline(deliverySnapshot(delivery));
      setBaselinePo(formatAttnPo(poNumber));
    }
  }, [delivery, editing, poNumber]);

  useEffect(() => {
    if (!forceValidate) return;
    setEditing(true);
    // Highlight errors on current delivery values — do not re-parse rawShipTo
    // (that would refill fields the admin cleared).
    setFieldErrors(getDeliveryFieldErrors(delivery));
  }, [forceValidate, delivery]);

  const beginEdit = () => {
    const next = toEditForm(delivery, poNumber);
    setForm(next);
    setBaseline(deliverySnapshot(delivery));
    setBaselinePo(formatAttnPo(poNumber));
    setFieldErrors({});
    setEditing(true);
  };

  const commitForm = (nextForm) => {
    const payload = buildDeliveryPayload(nextForm, poNumber);
    onSave?.(payload);
    // Sync page PO only when ATTN PO has a value — clearing the delivery
    // ATTN line must not wipe the order PO number.
    if (onPoNumberChange && payload.poNumber) {
      onPoNumberChange(payload.poNumber);
    }
  };

  const clearFieldError = (name) => {
    if (!fieldErrors[name]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    clearFieldError(name);
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      commitForm(next);
      return next;
    });
  };

  const handleUndo = () => {
    setForm(toEditForm(baseline, baselinePo));
    onSave?.(baseline);
    if (onPoNumberChange) onPoNumberChange(baselinePo);
    setFieldErrors({});
    setEditing(false);
  };

  const inputClass = (name) =>
    `delivery-summary__input${fieldErrors[name] ? " border border-danger" : ""}`;

  if (editing) {
    return (
      <div className="delivery-summary delivery-summary--editing">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="delivery-summary__title mb-0">Delivery</h6>
          <button
            type="button"
            className="delivery-summary__undo"
            onClick={handleUndo}
          >
            <UndoSvg /> undo
          </button>
        </div>

        <div className="row g-2 delivery-summary__form">
          <div className="col-12">
            <Form.Control
              className={inputClass("facilityName")}
              name="facilityName"
              value={form.facilityName}
              onChange={handleChange}
              placeholder="Facility / clinic name"
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("attnPo")}
              name="attnPo"
              value={form.attnPo}
              onChange={handleChange}
              placeholder="ATTN PO: …"
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("recipient")}
              name="recipient"
              value={form.recipient}
              onChange={handleChange}
              placeholder="Recipient"
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("address1")}
              name="address1"
              value={form.address1}
              onChange={handleChange}
              placeholder="Street address"
            />
          </div>
          <div className="col-12 col-md-5">
            <Form.Control
              className={inputClass("city")}
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder="City"
            />
          </div>
          <div className="col-6 col-md-3">
            <Form.Control
              className={inputClass("state")}
              name="state"
              value={form.state}
              onChange={handleChange}
              placeholder="State"
              maxLength={2}
            />
          </div>
          <div className="col-6 col-md-4">
            <Form.Control
              className={inputClass("zip")}
              name="zip"
              value={form.zip}
              onChange={handleChange}
              placeholder="ZIP"
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("country")}
              name="country"
              value={form.country || ""}
              onChange={handleChange}
              placeholder="Country"
            />
          </div>
          <div className="col-12">
            <Form.Control
              className={inputClass("phone")}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone (optional)"
            />
          </div>
        </div>
      </div>
    );
  }

  const shipToLines = buildShipToDisplayLines(delivery, poNumber);

  return (
    <div className="delivery-summary">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <h6 className="delivery-summary__ship-to mb-0">SHIP TO:</h6>
        {onSave ? (
          <button
            type="button"
            className="btn btn-link p-0 d-flex align-items-center gap-1 text-decoration-none"
            onClick={beginEdit}
          >
            <EditSvg /> Edit
          </button>
        ) : null}
      </div>
      <div className="delivery-summary__address">
        {shipToLines.length > 0 ? (
          shipToLines.map((line, index) => (
            <p key={`${index}-${line}`}>{line}</p>
          ))
        ) : (
          <p>—</p>
        )}
      </div>
    </div>
  );
}
